import axios from 'axios';
import { Globals } from '../../modules/Globals';
import {
  IDayCloseDetailResponse,
  IDayCloseListRow,
  IDayCloseRegenerateResponse,
  IDaySummaryRow,
} from '../../interfaces/IDayClose';

const apiUrl = Globals.apiPath + '/stats';

/** 'yyyy-MM-dd' from a local-midnight Date (no UTC shift). */
function toBusinessDateParam(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

export default class DayClosesService {
  static async list(params: {
    restaurantId: number;
    branchId: number;
    from: Date;
    to: Date;
  }): Promise<IDayCloseListRow[]> {
    const res = await axios.get<{ rows: IDayCloseListRow[] }>(
      `${apiUrl}/day-closes` +
        `?r=${params.restaurantId}` +
        `&b=${params.branchId}` +
        `&from=${toBusinessDateParam(params.from)}` +
        `&to=${toBusinessDateParam(params.to)}`
    );
    return res.data.rows;
  }

  static async get(params: {
    restaurantId: number;
    branchId: number;
    /** 'yyyy-MM-dd'. */
    businessDate: string;
    /** Computes a live preview instead of reading snapshots. */
    live?: boolean;
    /** Specific stored version (defaults to the latest). */
    version?: number;
  }): Promise<IDayCloseDetailResponse> {
    const res = await axios.get<IDayCloseDetailResponse>(
      `${apiUrl}/day-closes/${params.businessDate}` +
        `?r=${params.restaurantId}` +
        `&b=${params.branchId}` +
        (params.live ? '&live=1' : '') +
        (params.version ? `&version=${params.version}` : '')
    );
    return res.data;
  }

  static async regenerate(params: {
    restaurantId: number;
    branchId: number;
    /** 'yyyy-MM-dd'. */
    businessDate: string;
  }): Promise<IDayCloseRegenerateResponse> {
    const res = await axios.post<IDayCloseRegenerateResponse>(
      `${apiUrl}/day-closes/${params.businessDate}/regenerate` +
        `?r=${params.restaurantId}` +
        `&b=${params.branchId}`
    );
    return res.data;
  }

  /** Period grid: one row per business day with money activity (≤92 days). */
  static async getDaySummary(params: {
    restaurantId: number;
    branchId: number;
    from: Date;
    to: Date;
  }): Promise<IDaySummaryRow[]> {
    const res = await axios.get<{ rows: IDaySummaryRow[] }>(
      `${apiUrl}/day-summary` +
        `?r=${params.restaurantId}` +
        `&b=${params.branchId}` +
        `&from=${toBusinessDateParam(params.from)}` +
        `&to=${toBusinessDateParam(params.to)}`
    );
    return res.data.rows;
  }
}
